import { useState, useCallback } from 'react';
import { certificationAgreementService, RoleType } from '@/services/certificationAgreementService';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';

interface UseCertificationAgreementReturn {
  showModal: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
  checkAndShowModal: (roleType: RoleType) => Promise<boolean>;
  submitAgreement: (roleType: RoleType, acceptedAll: boolean) => Promise<boolean>;
  closeModal: () => void;
  currentRoleType: RoleType | null;
}

export const useCertificationAgreement = (): UseCertificationAgreementReturn => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentRoleType, setCurrentRoleType] = useState<RoleType | null>(null);

  const getCurrentUser = useCallback(() => {
    if (supabaseUser) {
      return { id: supabaseUser.id, type: 'supabase' as const };
    }
    if (internalUser) {
      return { id: internalUser.id, type: 'internal' as const };
    }
    return null;
  }, [supabaseUser, internalUser]);

  /**
   * Check if user has already accepted agreements for this role.
   * Returns true if user has already accepted (no modal needed).
   * Returns false and shows modal if user needs to accept.
   */
  const checkAndShowModal = useCallback(async (roleType: RoleType): Promise<boolean> => {
    const user = getCurrentUser();
    if (!user) {
      console.error('No user found for certification agreement check');
      return false;
    }

    setIsLoading(true);
    setCurrentRoleType(roleType);

    try {
      const existingAgreement = await certificationAgreementService.checkAgreementStatus(
        user.id,
        user.type,
        roleType
      );

      if (existingAgreement) {
        // User has already accepted all agreements for this role
        setIsLoading(false);
        return true;
      }

      // User needs to accept agreements
      setShowModal(true);
      setIsLoading(false);
      return false;
    } catch (error) {
      console.error('Error checking certification agreement:', error);
      setIsLoading(false);
      return false;
    }
  }, [getCurrentUser]);

  /**
   * Submit the certification agreement acceptance
   */
  const submitAgreement = useCallback(async (roleType: RoleType, acceptedAll: boolean): Promise<boolean> => {
    if (!acceptedAll) {
      return false;
    }

    const user = getCurrentUser();
    if (!user) {
      console.error('No user found for certification agreement submission');
      return false;
    }

    setIsSubmitting(true);

    try {
      const result = await certificationAgreementService.submitAgreement({
        userId: user.id,
        userType: user.type,
        roleType,
        platformParticipationAccepted: true,
        brandUsageAccepted: true,
        certificationTermsAccepted: true
      });

      setIsSubmitting(false);

      if (result.success) {
        setShowModal(false);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error submitting certification agreement:', error);
      setIsSubmitting(false);
      return false;
    }
  }, [getCurrentUser]);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setCurrentRoleType(null);
  }, []);

  return {
    showModal,
    isLoading,
    isSubmitting,
    checkAndShowModal,
    submitAgreement,
    closeModal,
    currentRoleType
  };
};
